import { ChangeDetectorRef } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { Radio, RadioFilterField } from './filter-field-radio';

type TranslationResult = string | { [key: string]: string } | any;

/**
 * This cannot safely be called before the AfterViewInit angular lifecycle hook.
 *
 * When attaching a translator, it will assume that the values given for
 * things like filter name and option name are actually translation keys, and
 * will use them as inputs during translation.
 *
 * Provide ChangeDetectorRef to inform the app when new values are set by the
 * translator during the change detection round.
 *
 * IMPORTANT: Calling this function will create observable subscriptions.  You
 * will need to clean them up during the OnDestroy lifecycle hook of your
 * filter component.  THe subscriptions are provided on the return value of
 * this function.
 */
export function subscribeRadioFilterFieldToTranslator(
  field: RadioFilterField,
  translateService: {
    instant: (key: string | Array<string>) => TranslationResult;
    stream: (key: string | Array<string>) => Observable<TranslationResult>;
  },
  changeDetector: ChangeDetectorRef,
): Subscription[] {
  const optionSubs = updateOptionNamesOnLanguageChange(field, (key) => translateService.stream(key), changeDetector);
  field.options = translateOptions(field, (key) => translateService.instant(key));
  return optionSubs;
}

function updateOptionNamesOnLanguageChange(
  field: RadioFilterField,
  stream: (key: string | Array<string>) => Observable<TranslationResult>,
  changeDetector: ChangeDetectorRef,
): Subscription[] {
  const subs: Subscription[] = [];
  const optionKeys = field.options;
  const keys = optionKeys.map((k) => k.name);
  subs.push(
    stream(keys).subscribe((translated) => {
      field.options = zipTranslated(optionKeys, translated);
      changeDetector.detectChanges();
    }),
  );
  return subs;
}

function translateOptions(
  field: RadioFilterField,
  instant: (key: string | Array<string>) => TranslationResult,
): Radio[] {
  const optionKeys = field.options;
  const keys = optionKeys.map((k) => k.name);
  const translated: { [key: string]: string } = instant(keys);
  return zipTranslated(optionKeys, translated);
}

/**
 * Replaces the names of the given radio options using the given translations.
 */
function zipTranslated(optionKeys: Radio[], translated: { [key: string]: string }): Radio[] {
  return optionKeys.map((key) => ({ name: translated[key.name], id: key.id }));
}
